/* global chrome */
import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion, isValidMotionProp } from 'framer-motion';
import { useColorMode } from '@chakra-ui/color-mode';
import { Box, Text } from '@chakra-ui/layout';
import { Tooltip } from '@chakra-ui/tooltip';
import { Icon } from '@chakra-ui/icon';
import RemindersModal from './RemindersModal';
import { chakra, shouldForwardProp, useColorModeValue } from '@chakra-ui/react';
import { useRemindersStore } from '../stores/remindersStore';
import useCustomToast from './ToastProvider';
import { tooltipAnimation, tooltipDownAnimation } from './customTheme';
import { MainButton } from './UiElements';

const ChakraBox = chakra(motion.div, {
    shouldForwardProp: (prop) =>
        isValidMotionProp(prop) || shouldForwardProp(prop)
});

const RemindersButton = () => {
    //Zustate
    const isLoadingUser = false; //TODO LOGIC useStore((state) => state.isLoadingUser);
    const setRemindersModal = useRemindersStore(
        // @ts-ignore
        (state) => state.setRemindersModal
    );
    const isRemindersModalOpen = useRemindersStore(
        (state: { isRemindersModalOpen: boolean }) => state.isRemindersModalOpen
    );
    const due = useRemindersStore((state: { due: any }) => state.due);
    //Colors
    const redBallShadow = useColorModeValue(
        '0 4px 4px -3px #00000050',
        '0 3px 2px -2px #00000090'
    );
    const hoverFilter = useColorModeValue(
        'brightness(1.075)',
        'brightness(1.225)'
    );

    const openModalClick = (e) => {
        //e.stopPropagation();
        setRemindersModal(true);
    };

    return (
        <>
            <Tooltip
                placement="top"
                rounded="6px"
                zIndex={1000}
                sx={{ transition: '0s' }}
                motionProps={tooltipDownAnimation}
                label={
                    <Box p="2px">
                        <Text>Reminders</Text>
                        {due.length > 0 && (
                            <Text
                                fontWeight="500"
                                fontSize="10px"
                                w="100%"
                                textAlign="center"
                                opacity="0.6"
                            >
                                {due.length === 1
                                    ? due.length + ' reminder due'
                                    : due.length + ' reminders due'}
                            </Text>
                        )}
                    </Box>
                }
            >
                {/* <Box
                    onClick={(e) => openModalClick(e)}
                    bg={bg}
                    boxShadow={mainButtonShadow}
                    cursor="pointer"
                    rounded="7px"
                    height="32px"
                    minWidth="32px"
                    fontWeight="600"
                    fontSize="18px"
                    maxWidth="32px"
                    mr="12px"
                    display="flex"
                    _hover={{
                        bg: hoverMainButton
                    }}
                    transition="0s"
                    position={'relative'}
                    role="group"
                >
                    <>
                        <Icon
                            opacity={isLoadingUser ? '0.75' : '1'}
                            transition="transform 0.20s"
                            name={'alarmIcon'}
                            color={letterColor}
                            //size="16px"
                            m="auto"
                            //size="18px"
                            w="15px"
                            h="16px"
                        /> */}
                <Box pos="relative" role="group" mr="12px">
                    <MainButton
                        icon={'reminders'}
                        onClick={openModalClick}
                        //noBg={true}
                    />
                    <AnimatePresence>
                        {due.length > 0 && (
                            <ChakraBox
                                onClick={openModalClick}
                                initial={{ scale: 0 }}
                                animate={{
                                    scale: 1,
                                    transition: {
                                        delay: 0.24,
                                        type: 'spring',
                                        stiffness: 240,
                                        damping: 20
                                    }
                                }}
                                exit={{ scale: 0 }}
                                bgImage="linear-gradient(194deg, #FC8181, #E53E3E)"
                                rounded="6px"
                                width="20px"
                                height="18px"
                                alignItems={'center'}
                                display={'flex'}
                                position="absolute"
                                top="-8px"
                                right="-8px"
                                boxShadow={redBallShadow}
                                _groupHover={{
                                    filter: hoverFilter
                                }}
                                zIndex={33}
                                cursor={'pointer'}
                            >
                                <Text
                                    fontSize="11px"
                                    fontWeight="bold"
                                    color="#FFF"
                                    textAlign="center"
                                    m="auto"
                                    userSelect={'none'}
                                >
                                    {due.length > 9 ? '9+' : due.length}
                                </Text>
                            </ChakraBox>
                        )}
                    </AnimatePresence>
                    <ReminderUpdater />
                </Box>
            </Tooltip>
        </>
    );
};

export default RemindersButton;

function sortReminders(reminders) {
    const currentDate = new Date();

    const due = [];
    const coming = [];

    if (reminders === 'noActivePlan') {
        return { due: [], coming: [] };
    }

    for (let reminder of reminders) {
        if (!reminder.date) {
            console.warn('Missing date in reminder:', reminder);
            continue; // skip this reminder and move to the next one
        }

        const reminderDate = new Date(reminder.date.replace(' ', 'T'));

        if (reminderDate <= currentDate) {
            due.push(reminder);
        } else {
            coming.push(reminder);
        }
    }

    return {
        due,
        coming
    };
}

/**
 * Get the time difference between the current time and a given date in a natural expression.
 * @param {string} dueDate - The due date in "YYYY-MM-DD HH:mm" format.
 * @returns {string} - A natural expression of the time difference.
 */
const getTimeDifference = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    //@ts-ignore
    const millisecondsDifference = now - due;
    const totalHours = Math.floor(millisecondsDifference / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = Math.floor((millisecondsDifference / (1000 * 60)) % 60); // Calculate minutes

    const dayText = days === 1 ? 'day' : 'days';
    const hourText = hours === 1 ? 'hour' : 'hours';
    const minuteText = minutes === 1 ? 'minute' : 'minutes';

    if (days > 0 && hours > 0) {
        return `${days} ${dayText} and ${hours} ${hourText} overdue`;
    } else if (days > 0) {
        return `${days} ${dayText} overdue`;
    } else if (hours > 0) {
        return `${hours} ${hourText} overdue`;
    } else if (minutes === 0) {
        return `Now`;
    } else {
        return `${minutes} ${minuteText} overdue`; // Return appropriate minute format
    }
};

const ReminderUpdater = () => {
    const { colorMode } = useColorMode();
    const setDue = useRemindersStore(
        (state: { setDue: Function }) => state.setDue
    );
    const setComing = useRemindersStore(
        (state: { setComing: Function }) => state.setComing
    );
    const remindersData = useRemindersStore(
        (state: { remindersData: unknown }) => state.remindersData
    );

    const prevDueRef = useRef([]);

    const showToast = useCustomToast();

    const TOASTTITLE = {
        group: 'Group',
        groupCustomIcon: 'Group',
        note: 'Note',
        stack: 'Stacked sites',
        site: 'Site',
        Site: 'Site'
    };

    const hasRunInitial = useRef(false);

    useEffect(() => {
        if (remindersData === 'loading') return;
        const checkReminders = (isInitialRun = false) => {
            const sortedReminders = sortReminders(remindersData);

            if (isInitialRun) {
                // Show toast for each due reminder on initial run.
                sortedReminders.due.map((item, i) => {
                    showToast({
                        title: TOASTTITLE[item.content.type]
                            ? `Reminder: ${TOASTTITLE[item.content.type]} due`
                            : 'Reminder due',
                        subtitle: `${getTimeDifference(item.date)}`,
                        type: 'reminder'
                    });
                });
            } else {
                // Find any new reminders that are now due but weren't before.
                const newDueReminders = sortedReminders.due.filter(
                    (reminder) =>
                        !prevDueRef.current.some(
                            (prevReminder) => prevReminder.id === reminder.id
                        )
                );

                // If there are any new due reminders, show a toast.
                newDueReminders.forEach((item) => {
                    showToast({
                        title: TOASTTITLE[item.content.type]
                            ? `Reminder: ${TOASTTITLE[item.content.type]} Due`
                            : 'Reminder Due',
                        subtitle: `${getTimeDifference(item.date)}`,
                        type: 'reminder'
                    });
                });
            }

            // Update the due and coming reminders in the state.
            setDue(sortedReminders.due);
            setComing(sortedReminders.coming);

            // Update the ref to keep track of the current due reminders.
            prevDueRef.current = sortedReminders.due;
        };

        if (!hasRunInitial.current) {
            checkReminders(true); // Initial run
            hasRunInitial.current = true; // Mark as having completed the initial run
        } else {
            checkReminders(false); // Subsequent runs
        }

        const intervalId = setInterval(() => checkReminders(false), 60000); // Regular checks
        return () => clearInterval(intervalId);
    }, [remindersData]);

    /* const spacesToShow = useStore.getState().spacesToShow;

    useEffect(() => {
        //compare here, return if remindersData === 'loading' or spacesToShow is empty
        if (remindersData !== 'loading' && spacesToShow.length) {
            const reminderSet = new Set(remindersData.map((data) => data.id));

            // Filter spacesToShow based on the reminderSet
            const matchingSpaces = spacesToShow.filter((space) =>
                space.tabs.some((tab) => reminderSet.has(tab.id))
            );
            console.log('matchingSpaces', matchingSpaces);
        }
    }, [spacesToShow, remindersData]); */

    return null;
};
